import { RequiredCaptchaEndpoints } from "~services/app/recaptcha";

import { ContainsAllCheck } from "../utils";

declare global {
  interface Window {
    grecaptcha?: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      ready: (callback: () => void) => void;
    };
  }
}

const siteKey = {
  production: "6LdQumEsAAAAAGweC2Eqf-EEuyI0YSgbFI8gboGl",
  staging: "6Lfyu2EsAAAAAD6ShAVvbKHReq-hL1qn9rTwvE1f",
}[process.env.NODE_ENV!];

let scriptLoadPromise: null | Promise<void> = null;

export const REQUIRED_CAPTCHA_ENDPOINTS = [
  "auth.signup.create",
  "auth.join.byEmail.do",
  "auth.join.inviteRequest.create",
  "auth.login.create",
] as const;

true satisfies ContainsAllCheck<(typeof REQUIRED_CAPTCHA_ENDPOINTS)[number], RequiredCaptchaEndpoints>;

export default async function getRecaptchaToken(endpoint: RequiredCaptchaEndpoints) {
  if (typeof siteKey === "undefined")
    return localStorage.getItem("fail_recaptcha") ? ":dev_recaptcha:failure:" : ":dev_recaptcha:success:";

  void loadRecaptchaScript();

  await waitForGrecaptcha();

  return new Promise<string>((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      window
        .grecaptcha!.execute(siteKey, { action: endpoint.replace(/\./g, "_") })
        .then(resolve)
        .catch(reject);
    });
  });
}

export async function loadRecaptchaScript() {
  if (typeof siteKey === "undefined") return;
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

async function waitForGrecaptcha(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();

  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (window.grecaptcha) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
}
